import { Box } from '@mui/material';
import { buildGoogleFontsUrl, findGoogleFamily } from '../builder/types';

type GroupMessageEmailTemplateViewProps = {
  body: string;
  height?: string | number;
  /**
   * Skala iframe-innehållet så ett ~600px-brett mail får plats i thumbnail-ytan.
   * Iframe renderas på sin normala upplösning men skalas ned visuellt via CSS-transform.
   * Default 1 (ingen skalning) bakåtkompatibelt med tidigare användningar.
   */
  scale?: number;
};

// Läser data-heading-font + data-body-font från envelope-tabellen. DOMParser
// används för att HTML-entiteter (t.ex. &quot; runt font-familjen) ska decodas
// korrekt — annars matchar värdet inte AVAILABLE_FONT_STACKS och Google Fonts
// laddas inte i iframen.
const readDocFonts = (body: string): { headingFont: string | null; bodyFont: string | null } => {
  if (!body) return { headingFont: null, bodyFont: null };
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<!DOCTYPE html><html><body>${body}</body></html>`, 'text/html');
    const table = doc.querySelector('table[data-email-doc]');
    return {
      headingFont: table?.getAttribute('data-heading-font') ?? null,
      bodyFont: table?.getAttribute('data-body-font') ?? null,
    };
  } catch {
    return { headingFont: null, bodyFont: null };
  }
};

// Bygger ett komplett HTML-dokument för iframen. Plockar font-stackar från
// envelope-tabellen och genererar ett färskt <link> till Google Fonts i <head>.
// På så sätt fungerar förhandsgranskningen även om <link>/<style>-taggarna i
// body har strippats av t.ex. TinyMCE i "Redigera innehåll"-läget.
const wrapBodyAsDocument = (body: string): string => {
  const { headingFont, bodyFont } = readDocFonts(body);
  const googleUrl = buildGoogleFontsUrl([headingFont ? findGoogleFamily(headingFont) : null, bodyFont ? findGoogleFamily(bodyFont) : null]);
  const fontLink = googleUrl ? `<link rel="stylesheet" href="${googleUrl}" />` : '';

  // Strippa eventuella kvarvarande font-tags i body — vi har redan plockat in
  // dem i <head> ovan, så ingen dubbel-laddning behövs.
  const linkRegex = /<link class="fs-fonts-link"[^>]*?>/g;
  const styleRegex = /<style class="fs-fonts-import"[^>]*?>[\s\S]*?<\/style>/g;
  const stripped = body.replace(linkRegex, '').replace(styleRegex, '');

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
${fontLink}
<style>
  html, body { margin: 0; padding: 0; }
  /* Göm scrollbars i thumbnail-iframes — innehåll får klippas av iframe-höjden. */
  html { scrollbar-width: none; -ms-overflow-style: none; }
  html::-webkit-scrollbar { display: none; }
</style>
</head>
<body>${stripped}</body>
</html>`;
};

export const GroupMessageEmailTemplateView = (props: GroupMessageEmailTemplateViewProps) => {
  const { body, height = '25rem', scale = 1 } = props;
  const docHtml = wrapBodyAsDocument(body);

  if (scale === 1) {
    return (
      <Box
        component='iframe'
        title='Email template preview'
        srcDoc={docHtml}
        sandbox=''
        sx={{ width: '100%', height, border: 0, display: 'block' }}
      />
    );
  }

  const inversePct = `${100 / scale}%`;
  return (
    <Box sx={{ width: '100%', height, overflow: 'hidden', position: 'relative' }}>
      <Box
        component='iframe'
        title='Email template preview'
        srcDoc={docHtml}
        sandbox=''
        sx={{
          width: inversePct,
          height: inversePct,
          border: 0,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
        }}
      />
    </Box>
  );
};
